import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchOnboardingExempt, leaveOnboarding } from '../../utils/onboardingExempt';

/**
 * Gate in front of the "What are you creating?" chooser (/onboarding/new).
 *
 * The Central "Welcome to LevelUp → Get Started" screen was removed at the
 * owner's request (2026-10-08): a new account goes straight to the chooser.
 * The gate keeps one job — Central platform admins (server-decided, DB flag)
 * skip Central onboarding entirely and go to the dashboard, or back to the app
 * sign-in they arrived with. Everyone else sees the chooser. Fail-soft: if the
 * admin check cannot be read, the chooser renders.
 */
type GateState = 'checking' | 'pass';

export function OnboardingWelcomeGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('checking');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (await fetchOnboardingExempt()) {
        if (!cancelled) leaveOnboarding(continueUrl, navigate);
        return;
      }
      if (!cancelled) setState('pass');
    })();
    return () => {
      cancelled = true;
    };
    // Decided once per visit to the chooser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'pass') return <>{children}</>;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div
        role="status"
        aria-label="Loading"
        className="animate-spin w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full"
      />
    </div>
  );
}
